<template>
  <div class="row" v-if="canSearch">
    <div class="card advanced-search width-100" style="padding: 0 5px;">
      <form class="form-horizontal">
        <div class="card-header">
          <h4 class="card-title" style="display:inline">
            Job Templates Search
          </h4>
        </div>
        <div class="card-body form-group">
          <div class="row">
            <div class="col-md-3">
              <label>Search</label>
              <el-input type="search"
                     placeholder="Search"
                     clearable
                     v-model="query.search"
                     @keyup.enter.native="fetchSearchResults"
                     v-validate="'minFields'"
                     data-vv-as="search"
                     data-vv-name="search"/>
              <span class="text-danger" v-show="errors.has('search')">
                {{ mainSearchFieldErrorText }}
            </span>
            </div>
            <div class="col-md-3">
              <label>Department(s)
                <span class="text-danger" v-show="errors.has('search')">
                {{ otherSearchFieldErrorText }}
              </span>
              </label>
              <el-select multiple class="select"
                         size="large"
                         clearable
                         placeholder="Department"
                         data-vv-as="departments"
                         v-model="query.departmentIds"
                         v-validate="'minFields'"
                         data-vv-name="search"
                         @clear="resetValidator">
                <el-option v-for="option in allDepartments"
                           class="select"
                           :value="option.value"
                           :label="option.label"
                           :key="option.label">
                </el-option>
              </el-select>
            </div>
            <div class="col-md-3">
              <label>Client
                <span class="text-danger" v-show="errors.has('search')">
                {{ otherSearchFieldErrorText }}
              </span>
              </label>
              <el-autocomplete
                style="width:100%"
                clearable
                name="client"
                ref="clientId"
                :debounce="150"
                v-model="selectedClient"
                :fetch-suggestions="findClients"
                :trigger-on-focus="false"
                value-key="name"
                @blur="verifyClientAutocompleteSelected"
                placeholder="Client"
                v-validate="'minFields'"
                data-vv-name="client"
                @select="setClient">
              </el-autocomplete>
              <small class="text-danger" v-show="showInvalidAutocompleteWarning('clientId')">
                {{ fieldNotValidError('Client') }}
              </small>
            </div>
            <div class="col-md-3">
              <label>Builder
                <span class="text-danger" v-show="errors.has('search')">
                {{ otherSearchFieldErrorText }}
              </span>
              </label>
              <el-autocomplete
                style="width:100%"
                clearable
                name="Builder"
                ref="builderId"
                v-model="selectedBuilder"
                :debounce="150"
                :fetch-suggestions="findBuilders"
                :trigger-on-focus="false"
                value-key="name"
                @blur="verifyBuilderAutocompleteSelected"
                placeholder="Builder"
                v-validate="'minFields'"
                data-vv-name="builder"
                @select="setBuilder">
              </el-autocomplete>
              <small class="text-danger" v-show="showInvalidAutocompleteWarning('builderId')">
                {{ fieldNotValidError('Builder') }}
              </small>
            </div>
          </div>
        </div><!-- card content -->
        <div class="card-footer">
          <button type="button" @click="fetchSearchResults" class="btn btn-fill btn-info pull-right btn-search" style="margin-right: 5px;">
            Search
          </button>
          <button type="button" id="btn-clear" @click="clearSearchAndResults" class="btn btn-fill btn-info pull-right btn-clear">
            Clear
          </button>
        </div>
        <div class="fix-search-container"></div>
      </form>
    </div><!-- end card -->
    <div class="col-md-12 card">
      <div class="card-content row">
        <div class="col-sm-12" style="padding: 10px 10px 0 10px;">
          <div v-if="hasResults" class="pull-right" style="margin-bottom: 6px;">
            <a @click.prevent="downloadExcel" href="#"><img src="static/img/icon-excel.jpg" title="Export to Excel" class="m-left-12"/></a>
          </div>
          <div v-if="loading" style="text-align: center;">
            <img src="static/img/loading.gif"/>
          </div>
          <el-table class="table-striped wrap-whole-words indexed-table-column-width"
                    v-else
                    :data="results"
                    border
                    :max-height="tableHeight(400)"
                    style="width: 100%">
            <el-table-column
              :width="indexColumnWidth"
              align="center"
              label="#"
              type="index">
            </el-table-column>
            <el-table-column
              label="Name"
              min-width="150"
              sortable
              :sort-method="(a, b) => callStringSort(a._source, b._source, 'name')">
              <template v-slot="props">
                <span class="invisible">{{ props.row._id }}</span>
                <a href="#" title="View/Edit Job" @click.prevent="editJobTemplate(props.row._id)">{{ lodash.get(props.row._source, 'name') }}</a>
              </template>
            </el-table-column>
            <el-table-column v-for="column in tableColumns"
                             :key="column.label"
                             :min-width="column.minWidth"
                             :width="column.width"
                             :prop="column.prop"
                             :sortable="column.sortable"
                             :sort-method="(a, b) => callStringSort(a._source, b._source, column.prop.replace('_source.', ''))"
                             :label="column.label">
            </el-table-column>
            <el-table-column
              v-if="canEdit"
              width="110"
              fixed="right"
              class="el-table__fixed"
              label="Actions">
              <template v-slot="props">
                <p-button type="success" size="sm" icon>
                  <i class="fa fa-edit" @click="editJobTemplate(props.row._id)"></i>
                </p-button>
                <p-button type="danger" size="sm" icon @click="confirmDelete(props.row)">
                  <i class="fa fa-times"></i>
                </p-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
        <div class="col-sm-6 pagination-info">
          <span class="category">Showing {{total}} entries</span>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import {mapGetters} from 'vuex'
import {
  autocompleteTextButNoValue,
  autocompleteTextButNoValueMessage,
  getTableHeight,
  notifyError,
  notifySuccess,
  setIndexColumnWidth,
  stringSort,
  verifyAutocompleteSelected
} from 'src/utilities/utils'
import Constants from 'src/utilities/constants'
import lodash from 'lodash'
import {minFields} from 'src/utilities/validation'
import PButton from 'src/components/UIComponents/Button.vue'
import Permissions from 'src/utilities/permissions'
import {NO_CONTENT} from 'http-status-codes'
import {get, sync} from 'vuex-pathify'

  export default{
    components: {
      PButton
    },
    computed: {
      query: sync('jobTemplates@query'),
      results: get('jobTemplates@results'),
      ...mapGetters({
        autocompleteClients: 'companies/autocompleteResultsClient',
        autocompleteBuilders: 'companies/autocompleteResultsBuilder',
        departments: 'departments/results',
        allDepartments: 'departments/kvpsForSelect',
        user: 'security/user'
      }),
      indexColumnWidth() {
        return setIndexColumnWidth(this.results.length)
      },
      total() {
        return this.results.length
      },
      hasResults() {
        return this.total > 0
      },
      canSearch() {
        return this.$can('read', Permissions.JOB_TEMPLATES)
      },
      canEdit() {
        return this.$can('edit', Permissions.JOB_TEMPLATES)
      }
    },
    data() {
      return {
        lodash,
        toDelete: null,
        selectedClient: null,
        selectedBuilder: null,
        constants: Constants,
        tableColumns: [
          {
            prop: '_source.jobName',
            label: 'Job Name',
            minWidth: 130,
            sortable: true
          },
          {
            prop: '_source.clientName',
            label: 'Client',
            minWidth: 130,
            sortable: true
          },
          {
            prop: '_source.builderName',
            label: 'Builder',
            minWidth: 130,
            sortable: true
          }
        ],
        mainSearchFieldErrorText: Constants.MESSAGES.AT_LEAST_ONE_FIELD_REQUIRED,
        otherSearchFieldErrorText: '*',
        loading: false
      }
    },
    methods: {
      downloadExcel() {
        const columns = [ {
          prop: '_source.name',
          label: 'Name',
          minWidth: 130,
        }, ...this.tableColumns]
        this.$store.dispatch('jobTemplates/resultsToExcel',
          {
            columns,
            data: this.results,
            user: this.user
          }
        )
      },
      fieldNotValidError(field) {
        return autocompleteTextButNoValueMessage(field)
      },
      showInvalidAutocompleteWarning(refAndPathName) {
        // $ref has to match prop in store
        return autocompleteTextButNoValue(this, refAndPathName, this.$store.get(`jobTemplates@query/${refAndPathName}`))
      },
      editJobTemplate(jobTemplateId) {
        this.$store.commit('jobTemplates/resetEdit')
        this.$eventHub.$emit('show-modal-2', 'Job Template', {id: jobTemplateId}, '800px')
      },
      clearSearchAndResults() {
        this.$store.commit('jobTemplates/resetResults')
        this.$store.commit('jobTemplates/resetQuery')
        this.$validator.reset()
        this.resetClientAndBuilder()
      },
      resetClientAndBuilder() {
        this.selectedClient = null
        this.selectedBuilder = null
      },
      updateSearchResults(jobTemplate) {
        this.$store.set('jobTemplates/update', jobTemplate)
        this.$forceUpdate()
      },
      findClients(text, cb) {
        this.$store.set('jobTemplates/query@clientId', null)
        this.findCompany(text, Constants.COMPANY.TYPES.CLIENT)
        cb(this.autocompleteClients)
      },
      findBuilders(text, cb) {
        this.$store.set('jobTemplates/query@builderId', null)
        this.findCompany(text, Constants.COMPANY.TYPES.BUILDER)
        cb(this.autocompleteBuilders)
      },
      setClient(client) {
        if (client) {
          this.$store.set('jobTemplates/query@clientId', client.id)
          this.$store.commit('companies/autocompleteResultsClient', [])
          this.$validator.reset()
        }
      },
      verifyClientAutocompleteSelected() {
        verifyAutocompleteSelected(this, 'jobTemplates@query.clientId', 'client', this.selectedClient)
      },
      setBuilder(builder) {
        if (builder) {
          this.$store.set('jobTemplates/query@builderId', builder.id)
          this.$store.commit('companies/autocompleteResultsBuilder', [])
          this.$validator.reset()
        }
      },
      verifyBuilderAutocompleteSelected() {
        verifyAutocompleteSelected(this, 'jobTemplates@query.builderId', 'builder', this.selectedBuilder)
      },
      tableHeight(buffer) {
        return getTableHeight(buffer)
      },
      findCompany(text, type) {
        if (text) {
          const payload = {
            type: type,
            name: text
          }
          this.$store.dispatch('companies/fetchByNameStartsWithAndType', payload)
        }
      },
      async fetchSearchResults() {
        if (await this.$validator.validateAll()) {
          this.loading = true
          await this.$store.dispatch('jobTemplates/fetchSearchResults')
          this.loading = false
          if (this.results.length === 0) {
            this.$eventHub.$emit('show-modal-1', 'No Results Found', null, '500px')
          }
        }
      },
      resultsNoValidate() {
        this.$store.dispatch('jobTemplates/fetchSearchResults')
      },
      async confirmDelete(searchEntity) {
        const message = `Are you sure you want to delete job template <strong>${searchEntity._source.name}</strong>?`

        this.toDelete = searchEntity._id
        const payload = {
          message,
          entity: searchEntity._source,
          delete: this.delete,
          showButton: true
        }

        this.$eventHub.$emit('show-modal-1', 'Confirm Delete', payload, '600px')
      },
      async delete() {
        const response = await this.$store.dispatch('delete', {
          endpoint: 'job-templates',
          id: this.toDelete
        })

        if (NO_CONTENT === response.status) {
          this.$store.commit('jobTemplates/delete', this.toDelete)
          notifySuccess(this, 'Job Template Deleted Successfully')
        } else {
          notifyError(this, 'Error Deleting Job Template')
        }
      },
      resetValidator() {
        this.$validator.reset()
      },
      callStringSort(a, b, prop) {
        return stringSort(a, b, prop)
      },
    },
    watch: {
      selectedBuilder: {
        handler: function (builderId) {
          if (!builderId) {
            this.$store.set('jobTemplates/query@builderId', null)
            this.selectedBuilder = null
            this.$validator.reset()
          }
        }
      },
      selectedClient: {
        handler: function (clientId) {
          if (!clientId) {
            this.$store.set('jobTemplates/query@clientId', null)
            this.selectedClient = null
            this.$validator.reset()
          }
        }
      },
    },
    async beforeCreate() {
      await this.$store.cache.dispatch('tasks/names/fetchAll')
    },
    mounted() {
      this.$eventHub.$on('jobTemplateSaved', this.updateSearchResults)
      minFields('jobTemplates')
      this.resetValidator()
    },
    beforeDestroy() {
      this.$eventHub.$off('jobTemplateSaved')
    }
  }
</script>
<style scoped>
  #btn-clear {
    margin: 0 12px;
  }
</style>
